/**
 * usePatchBirthData — mutation for `PATCH /api/v1/birth-data`.
 *
 * Paired with backend T20 — updates the birth fields on the current user
 * WITHOUT resetting `onboardingStep`. Used by Settings → Account when the
 * user edits their birth-data after onboarding completion.
 *
 * Invalidates `['me']` on success so the cached profile re-renders the new
 * birth values immediately, and `['charts']` / `['chart']` so the own chart in
 * «Мои карты» is not shown stale.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export type TimePrecision = 'EXACT' | 'MORNING' | 'AFTERNOON' | 'EVENING' | 'NIGHT';

export interface PatchBirthDataBody {
  date?: string;
  timePrecision?: TimePrecision;
  timeExact?: string | null;
  placeName?: string;
  lat?: number;
  lng?: number;
  tz?: string;
}

export function usePatchBirthData(): {
  mutate: (body: PatchBirthDataBody) => void;
  mutateAsync: (body: PatchBirthDataBody) => Promise<{ ok: true; onboardingStep: number }>;
  isPending: boolean;
  isError: boolean;
} {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();

  const m = useMutation<{ ok: true; onboardingStep: number }, Error, PatchBirthDataBody>({
    mutationFn: async (body) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/birth-data', jwt, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`patch_birth_data_failed: ${res.status} ${text}`);
      }
      return (await res.json()) as { ok: true; onboardingStep: number };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      // The own chart («Я») in «Мои карты» is computed from these fields — drop its cached copies.
      void qc.invalidateQueries({ queryKey: ['charts'] });
      void qc.invalidateQueries({ queryKey: ['chart'] });
    },
  });

  return { mutate: m.mutate, mutateAsync: m.mutateAsync, isPending: m.isPending, isError: m.isError };
}
